import { useEffect, useState } from "react";
import ProductImage from "@/components/ProductImage";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocation } from "@/contexts/LocationContext";
import { useWishlist } from "@/contexts/WishlistContext";
import HeartButton from "@/components/HeartButton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Heart } from "lucide-react";

// Wishlist: products the customer saved with the heart, priced near them.
export default function Wishlist({ embedded = false }) {
  const { locationParams, locationLabel } = useLocation();
  const wish = useWishlist();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/customer/wishlist", { params: locationParams })
      .then((r) => { if (live) setRows(r.data || []); })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        if (live) setRows((x) => x || []);
      });
    return () => { live = false; };
  }, [locationParams]);

  // A heart removed here takes the product off straight away.
  const list = (rows || []).filter((p) => wish.has(p));

  return (
    <div className={embedded ? "" : "max-w-6xl mx-auto px-4 sm:px-6 py-8"} data-testid="wishlist-page">
      <div className="mb-6">
        {!embedded && <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Wishlist</h1>}
        <p className="text-sm text-stone-600 mt-2">{list.length ? `${list.length} saved · prices from shops near ${locationLabel}` : "Products you saved for later."}</p>
      </div>

      {rows === null ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Card key={i} className="aspect-[3/4] rounded-2xl animate-pulse bg-stone-100" />)}
        </div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center rounded-3xl border-dashed">
          <div className="h-14 w-14 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center mx-auto mb-4"><Heart className="h-6 w-6" /></div>
          <div className="font-heading text-xl font-semibold">Nothing saved yet</div>
          <p className="text-sm text-stone-600 mt-2">Click the heart on any product to save it here for later.</p>
          <Button asChild className="mt-5 rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold"><Link to="/shop">Browse products</Link></Button>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="wishlist-grid">
          {list.map((p) => (
            <Link key={p.id} to={`/product/${p.id}`} data-testid={`wishlist-product-${p.id}`}>
              <Card className="overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all border-stone-200 rounded-2xl group h-full flex flex-col">
                <div className="relative aspect-square bg-stone-100 overflow-hidden">
                  <ProductImage src={p.image_url} alt={p.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${p.best_offer ? "" : "opacity-60"}`} />
                  <HeartButton p={p} className="absolute top-2 right-2" />
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-wide text-stone-500 truncate">{[p.brand, p.size_label].filter(Boolean).join(" · ")}</div>
                  <div className="text-sm font-semibold mt-1 line-clamp-2 leading-tight flex-1">{p.name}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {p.best_offer ? (
                      <div>
                        <span className="font-heading text-lg font-bold text-stoneWarm">₹{p.best_offer.price}</span>
                        {p.best_offer.mrp > p.best_offer.price && <span className="ml-1 text-xs text-stone-400 line-through">₹{p.best_offer.mrp}</span>}
                      </div>
                    ) : (
                      <span className="text-xs font-semibold text-terracotta">Not available near you</span>
                    )}
                    {p.size_count > 1 && <Badge variant="outline" className="rounded-full text-[10px] border-stone-300 text-stone-600">{p.size_count} sizes</Badge>}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
